// File: src/utils/semanticValidation.js
// Client-side semantic validation for analysis task configs (Issue #64)
// Mirrors the backend validation in defo-ads-api/src/lib/agent/semantic-validator.js

// Tool membership + read/write classification are now authoritative on the
// backend (validated against the shared Tool Core catalog). This client-side
// preview no longer hardcodes a tool list; it classifies writes by name prefix,
// matching the MCP catalog verbs (create_/update_/delete_/add_/attach_/…).
const WRITE_PREFIXES = ['create_', 'update_', 'delete_', 'add_', 'remove_', 'toggle_', 'move_', 'attach_', 'detach_', 'set_', 'cancel_', 'trigger_', 'unlink_'];
const isWriteTool = (name) => WRITE_PREFIXES.some((p) => name.startsWith(p));

/**
 * Validate an analysis task config semantically.
 * @param {object} config - The analysis task configuration
 * @returns {{ errors: Array, warnings: Array, info: Array }}
 */
export function validateTaskConfig(config) {
    const errors = [];
    const warnings = [];
    const info = [];

    if (!config) return { errors: [{ rule: 'null_config', message: 'No configuration provided', severity: 'error', field: null }], warnings, info };

    const enabledTools = config.enabledTools || [];
    const prompt = config.systemPrompt || '';
    const triggerType = config.triggerType || 'schedule';
    const channels = config.channels || ['in_app'];
    const notificationType = config.notificationType || 'AGENT_MESSAGE';

    // Rule 1 (tool catalog membership) is enforced authoritatively on the
    // backend against the Tool Core; the client preview no longer duplicates it.

    // Rule 2: Write tools + AGENT_MESSAGE
    const hasWriteTools = enabledTools.some(isWriteTool);
    if (hasWriteTools && notificationType === 'AGENT_MESSAGE') {
        warnings.push({
            rule: 'write_tools_agent_message',
            message: 'Write tools are enabled but notificationType is AGENT_MESSAGE. Proposed actions won\'t be shown as actionable cards.',
            severity: 'warning',
            field: 'notificationType',
            suggestedFix: 'Switch notificationType to AGENT_RECOMMENDATION',
        });
    }

    // Rule 4: Empty enabledTools
    if (enabledTools.length === 0) {
        info.push({
            rule: 'empty_enabled_tools',
            message: 'No tools are enabled. The AI will only generate text responses without accessing user data.',
            severity: 'info',
            field: 'enabledTools',
        });
    }

    // Rule 5: Event trigger without delay
    if (triggerType === 'event' && !config.delayAfterTrigger) {
        info.push({
            rule: 'event_trigger_no_delay',
            message: 'Event trigger has no delay. The task will execute immediately when the event fires.',
            severity: 'info',
            field: 'delayAfterTrigger',
        });
    }

    // Rule 6: Email without subject
    if (channels.includes('email')) {
        const emailSubject = config.emailSubjectTemplate;
        const hasSubject = emailSubject && typeof emailSubject === 'object' && Object.keys(emailSubject).length > 0;
        if (!hasSubject) {
            errors.push({
                rule: 'email_without_subject',
                message: 'Email channel is enabled but no email subject template is configured',
                severity: 'error',
                field: 'emailSubjectTemplate',
                suggestedFix: 'Add email subject templates (at least "en" locale)',
            });
        }
    }

    // Rule 7: No template variables in prompt
    if (prompt && !prompt.match(/\{\{[^}]+\}\}/)) {
        info.push({
            rule: 'no_template_variables',
            message: 'System prompt does not use any {{template}} variables. Consider adding {{user.name}} for personalization.',
            severity: 'info',
            field: 'systemPrompt',
        });
    }

    // Rule 8: Quiet hours not set for email
    if (channels.includes('email') && !config.quietHoursStart) {
        info.push({
            rule: 'email_no_quiet_hours',
            message: 'Email channel is enabled but quiet hours are not configured.',
            severity: 'info',
            field: 'quietHoursStart',
        });
    }

    // Rule 9: Very high temperature
    if (config.temperature !== undefined && config.temperature > 1.5) {
        warnings.push({
            rule: 'high_temperature',
            message: `Temperature is set to ${config.temperature}. Values above 1.5 may produce inconsistent outputs.`,
            severity: 'warning',
            field: 'temperature',
            suggestedFix: 'Consider lowering temperature to 0.7-1.0',
        });
    }

    // Rule 10: Very low maxTokens
    if (config.maxTokens !== undefined && config.maxTokens < 200) {
        warnings.push({
            rule: 'low_max_tokens',
            message: `maxTokens is set to ${config.maxTokens}. This may result in truncated responses.`,
            severity: 'warning',
            field: 'maxTokens',
            suggestedFix: 'Consider increasing maxTokens to at least 500',
        });
    }

    // Rule 11: Condition rules with no schedule
    if (triggerType === 'condition' && !config.interval) {
        warnings.push({
            rule: 'condition_no_schedule',
            message: 'Trigger type is "condition" but no interval is set.',
            severity: 'warning',
            field: 'interval',
            suggestedFix: 'Set an interval (e.g., "24h")',
        });
    }

    // Rule 12: Audience rules + plans conflict
    const audienceRules = config.audienceRules || [];
    const subscriptionPlans = config.subscriptionPlans || ['all'];
    const hasSubscriptionAudienceRule = audienceRules.some(ruleGroup =>
        (ruleGroup.conditions || []).some(c => c.field && c.field.startsWith('subscription.'))
    );
    if (hasSubscriptionAudienceRule && subscriptionPlans.includes('all')) {
        info.push({
            rule: 'audience_plans_conflict',
            message: 'Audience rules reference subscription fields but subscriptionPlans is set to "all".',
            severity: 'info',
            field: 'audienceRules',
        });
    }

    return { errors, warnings, info };
}
